import { useLayoutEffect, useRef } from "react";
import { formatDate } from "@/utils/datetime";
import { ArrowRight } from "@phosphor-icons/react/dist/ssr";
import { motion } from "motion/react";
import Link from "next/link";
import { Badge } from "../ui/badge";
import gsap from "gsap";

const MotionLink = motion(Link);

const GuideCard = ({ data, size, label = "Read Now" }) => {
  const titleRef = useRef(null);

  useLayoutEffect(() => {
    if (!titleRef.current) return;

    let split = null;

    // Dynamic import to avoid SSR issues with ESM-only module
    import("gsap/SplitText").then(({ default: SplitText }) => {
      gsap.registerPlugin(SplitText);
      split = new SplitText(titleRef.current, {
        type: "lines",
        linesClass: "line",
      });
    });

    return () => {
      if (split) split.revert();
    };
  }, [data.title]);

  return (
    <MotionLink
      href={`/guides/${data.slug}`}
      key={`guides-${data.uuid}`}
      className={`${size} relative block group p-4 h-full lg:p-6 pb-2 overflow-hidden rounded-xl transition-colors bg-[#ccff33] hover:bg-[#E936AC]`}
      initial='rest'
      whileHover='hover'
      animate='rest'
    >
      {/* Content */}
      <span className='relative z-10 flex flex-col h-full'>
        <span className='flex-1'>
          {data.category?.name && (
            <span className='flex gap-1 mb-4 -mt-2 -ml-2'>
              <Badge className='h-auto py-1 text-white bg-background/30 hover:bg-background/30'>
                {data.category.name}
              </Badge>
            </span>
          )}

          {/* TITLE */}
          <span
            ref={titleRef}
            className='block text-2xl lg:text-[32px] font-semibold leading-[1.1] tracking-[-0.03em] font-neon uppercase'
          >
            {data.title}
          </span>
        </span>

        <span className='flex items-center justify-between gap-4 mt-3 -mr-3 text-xs transition text-background lg:mt-4'>
          <span className='opacity-60'>
            {formatDate(data.publish_date, "DD MMMM YYYY")}
          </span>
          <span className='flex-1 h-px transition bg-background/20' />

          <span className='flex items-center gap-3 p-1 bg-white rounded-full text-background'>
            <span className='text-[11px] font-medium font-display tracking-wide uppercase pl-3'>
              {label}
            </span>

            <span className='inline-flex items-center justify-center rounded-full bg-background size-7'>
              <ArrowRight weight='bold' className='text-white size-4' />
            </span>
          </span>
        </span>
      </span>
    </MotionLink>
  );
};

export default GuideCard;
